import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { api_url } from "../config/config";

const plusPattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`;

/* ── Safe slug ── */
const toSlug = (name) => {
  if (!name || typeof name !== "string") return "";
  return name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

/* ════════════════════════════════════════
   FALLBACK SVG — pharmacy pill illustration
   Fills 100% width & height of its container
════════════════════════════════════════ */
const BrandFallback = () => (
  <svg width="100%" height="100%" viewBox="0 0 100 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="38" r="30" fill="#eaf4f3" opacity="0.5"/>
    {/* Main pill */}
    <rect x="12" y="28" width="76" height="20" rx="10" fill="#eaf4f3" stroke="#c5d5d4" strokeWidth="1.5"/>
    <path d="M12 38C12 32.5 16.5 28 22 28H50V48H22C16.5 48 12 43.5 12 38Z" fill="#5fbdb3" fillOpacity="0.55"/>
    <path d="M88 38C88 43.5 83.5 48 78 48H50V28H78C83.5 28 88 32.5 88 38Z" fill="#ff601e" fillOpacity="0.45"/>
    <line x1="50" y1="28" x2="50" y2="48" stroke="white" strokeWidth="1.2" strokeDasharray="2.5 2"/>
    {/* Small pill TL */}
    <ellipse cx="24" cy="17" rx="8" ry="4.5" fill="#eaf4f3" stroke="#5fbdb3" strokeWidth="1.2"/>
    <line x1="24" y1="12.5" x2="24" y2="21.5" stroke="#5fbdb3" strokeWidth="1" strokeDasharray="2 1.5"/>
    {/* Small pill BR */}
    <ellipse cx="76" cy="60" rx="8" ry="4.5" fill="#ffd5c2" stroke="#ff601e" strokeWidth="1.2"/>
    <line x1="76" y1="55.5" x2="76" y2="64.5" stroke="#ff601e" strokeWidth="1" strokeDasharray="2 1.5"/>
    {/* Cross TR */}
    <rect x="77" y="11" width="3" height="12" rx="1.5" fill="#ff601e" fillOpacity="0.4"/>
    <rect x="72" y="16" width="13" height="3" rx="1.5" fill="#ff601e" fillOpacity="0.4"/>
    {/* Cross BL */}
    <rect x="16" y="56" width="3" height="12" rx="1.5" fill="#5fbdb3" fillOpacity="0.45"/>
    <rect x="11" y="61" width="13" height="3" rx="1.5" fill="#5fbdb3" fillOpacity="0.45"/>
  </svg>
);

/* ════════════════════════════════════════
   BRAND CARD — per-card img error state
════════════════════════════════════════ */
const BrandCard = ({ item, index, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const hasImage = item.src && !imgError;

  const isOrange  = index % 2 === 1;
  const cardBg    = isOrange ? "bg-gradient-to-b from-[#fff3ee] to-white" : "bg-gradient-to-b from-[#eaf4f3] to-white";
  const cardBorder= isOrange ? "border-[#ffd5c2] hover:border-[#ff601e]/60" : "border-[#c5d5d4] hover:border-[#5fbdb3]";
  const accentGrad= isOrange ? "from-[#ff601e] to-[#e55519]" : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
  const imgBg     = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
  const nameCls   = isOrange ? "text-[#c44a14] group-hover:text-[#ff601e]" : "text-[#1e4a46] group-hover:text-[#3a7a74]";
  const stripBg   = isOrange ? "bg-[#fff8f5]/90 border-[#ffd5c2]/40" : "bg-[#f4fbfa]/90 border-[#d4ecea]/40";
  const dotCls    = isOrange ? "bg-[#ff601e]" : "bg-[#5fbdb3]";
  const shadow    = isOrange
    ? "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]"
    : "shadow-[0_2px_12px_rgba(95,189,179,0.10)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]";

  return (
    <div
      onClick={onClick}
      className={`group border-[1.5px] ${cardBorder} ${cardBg} ${shadow}
        rounded-2xl overflow-hidden flex flex-col cursor-pointer relative
        hover:-translate-y-2 transition-all duration-300`}
      style={{ animation: `fadeInUp 0.35s ease ${(index % 12) * 35}ms both` }}
    >
      {/* Top accent */}
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentGrad}
        opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`} />

      {/* Corner dot */}
      <div className={`absolute top-2.5 right-2.5 w-2 h-2 rounded-full ${dotCls}
        opacity-20 group-hover:opacity-65 group-hover:scale-125 transition-all duration-300 z-10`} />

      {/* ── Image area — fallback fills full box ── */}
      <div className={`w-full h-[100px] sm:h-[116px] lg:h-[128px] ${imgBg} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
        {hasImage ? (
          <img
            src={item.src}
            alt={item.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          /* Fallback fills the entire box, no padding */
          <div className="w-full h-full">
            <BrandFallback />
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-black/[0.05]" />

      {/* Name strip */}
      <div className={`px-3 py-2.5 flex items-center justify-between gap-2 border-t ${stripBg}`}>
        <p className={`text-[11px] sm:text-[12px] font-semibold leading-snug line-clamp-1 flex-1 transition-colors duration-200 ${nameCls}`}>
          {item.name || "Shop By Brand"}
        </p>
        {/* Arrow slides in on hover */}
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
          stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 -translate-x-1.5 group-hover:translate-x-0 transition-all duration-200">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   PAGE
════════════════════════════════════════ */
const ShopBrand = () => {
  const navigate = useNavigate();
  const [manufacturers,         setManufacturers]         = useState([]);
  const [filteredManufacturers, setFilteredManufacturers] = useState([]);
  const [searchTerm,            setSearchTerm]            = useState("");
  const [currentPage,           setCurrentPage]           = useState(1);
  const [totalPages,            setTotalPages]            = useState(1);
  const [isLoading,             setIsLoading]             = useState(true);
  const [totalManufacturers,    setTotalManufacturers]    = useState(0);

  const fetchManufacturers = (page) => {
    setIsLoading(true);
    axios.get(`${api_url}/manufacturer?page=${page}&limit=20`)
      .then((response) => {
        if (response.data?.results) {
          const formatted = response.data.results
            .filter((m) => m && m.manufacturer_id)
            .map((m) => ({ src: m.image_url, name: m.name || "", manufacturer_id: m.manufacturer_id }));
          setManufacturers(formatted);
          setFilteredManufacturers(formatted);
          setTotalPages(Math.ceil(response.data.totalDocuments / 20));
          setTotalManufacturers(response.data.totalDocuments);
        }
      })
      .catch((err) => console.error("Error fetching manufacturers:", err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchManufacturers(currentPage); }, [currentPage]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSearch = (e) => {
    const val = e.target.value.toLowerCase();
    setSearchTerm(val);
    setFilteredManufacturers(manufacturers.filter((m) => m.name.toLowerCase().includes(val)));
  };

  const handleViewAll = async () => {
    setIsLoading(true);
    setSearchTerm("");
    try {
      const res = await axios.get(`${api_url}/manufacturer?limit=200`);
      if (res.data?.results) {
        const formatted = res.data.results
          .filter((m) => m && m.manufacturer_id)
          .map((m) => ({ src: m.image_url, name: m.name || "", manufacturer_id: m.manufacturer_id }));
        setManufacturers(formatted);
        setFilteredManufacturers(formatted);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching all manufacturers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManufect = (manufacturer) => {
    if (!manufacturer) return;
    const slug = toSlug(manufacturer.name) || manufacturer.manufacturer_id;
    if (!slug) return;
    navigate(`/shop-by-brand/${slug}`, {
      state: { manufacturer_id: manufacturer.manufacturer_id, manufacturer_name: manufacturer.name || "" },
    });
  };

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins overflow-x-hidden">
      <Helmet>
        <title>All Brands | Online Medical Store | MediSYS Pharmacy</title>
        <meta name="description" content="MediSYS Pharmacy - Buy medicines online and experience fastest home delivery." />
        <meta name="keywords" content="pharmacy, medical stores, online pharmacy, online pharmacy India, online medical store hyderabad" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Online Medical Store | MediSYS Pharmacy" />
        <meta property="og:description" content="Buy medicines online from MediSYS Pharmacy and experience fastest home delivery." />
      </Helmet>

      {/* ══════════════════════════════════════
          HERO BANNER
      ══════════════════════════════════════ */}
      <div className="relative w-full overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a1715 0%, #162c2a 50%, #1e4a46 100%)" }}>

        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: plusPattern, backgroundRepeat: "repeat", backgroundSize: "40px 40px" }} />

        {/* Pharmacy cross — top right */}
        <div className="absolute top-4 right-8 opacity-[0.07] pointer-events-none hidden sm:block">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
            <rect x="48" y="8"  width="24" height="104" rx="12" fill="white"/>
            <rect x="8"  y="48" width="104" height="24"  rx="12" fill="white"/>
          </svg>
        </div>

        {/* Pill — bottom left */}
        <div className="absolute -bottom-4 -left-6 opacity-[0.07] pointer-events-none hidden lg:block">
          <svg width="200" height="80" viewBox="0 0 200 80" fill="none">
            <rect x="4" y="14" width="192" height="52" rx="26" fill="white"/>
            <path d="M4 40C4 25.6 15.6 14 30 14H100V66H30C15.6 66 4 54.4 4 40Z" fill="white"/>
          </svg>
        </div>

        <div className="absolute -top-16 right-1/4 w-72 h-72 rounded-full bg-[#5fbdb3]/12 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-56 h-56 rounded-full bg-[#ff601e]/08 blur-3xl pointer-events-none" />

        <div className="relative px-5 sm:px-8 lg:px-14 py-10 sm:py-12">
          <nav className="flex items-center gap-2 text-[12px] font-medium text-white/40 mb-5">
            <Link to="/home" className="hover:text-white/70 transition-colors">Home</Link>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-white/70">Shop by Brands</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Explore</span>
              </div>
              <h1 className="font-bold text-[26px] sm:text-[36px] lg:text-[44px] text-white leading-tight">
                Shop by <span className="text-[#5fbdb3]">Brands</span>
              </h1>
              <p className="mt-2 text-[13px] text-white/50 max-w-[380px]">
                Browse medicines from your trusted pharmaceutical brands — all verified and authentic.
              </p>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 bg-white/[0.09] border border-white/[0.15] rounded-xl px-4 h-11 w-full sm:w-[300px] focus-within:bg-white/[0.13] focus-within:border-[#5fbdb3]/60 transition-all duration-200 flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input type="text" placeholder="Search brands…" value={searchTerm} onChange={handleSearch}
                className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder-white/35" />
              {searchTerm && (
                <button onClick={() => { setSearchTerm(""); setFilteredManufacturers(manufacturers); }}
                  className="text-white/40 hover:text-white text-lg leading-none transition-colors">×</button>
              )}
            </div>
          </div>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-4 mt-7">
            {[
              { val: isLoading ? "—" : `${totalManufacturers}+`, label: "Total Brands" },
              { val: "100%", label: "Verified" },
              { val: "Same Day", label: "Delivery" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2 bg-white/[0.07] border border-white/[0.1] rounded-xl px-4 py-2.5">
                <p className="font-bold text-[15px] text-white leading-none">{s.val}</p>
                <p className="text-[10px] text-white/40">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#f8fafa] to-transparent" />
      </div>

      {/* ══════════════════════════════════════
          CONTENT
      ══════════════════════════════════════ */}
      <div className="px-4 sm:px-8 lg:px-14 py-8">

        {/* Section heading */}
        <div className="flex items-center gap-4 mb-6">
          <h2 className="font-bold text-[16px] sm:text-[18px] text-[#1e4a46]">
            {searchTerm ? `Results for "${searchTerm}"` : "All Brands"}
          </h2>
          <div className="flex-1 h-px bg-[#e2eaea]" />
          {!isLoading && (
            <span className="text-[12px] font-medium text-[#8faba9] bg-[#eaf4f3] border border-[#d4ecea] rounded-full px-3 py-1 flex-shrink-0">
              {filteredManufacturers.length} brand{filteredManufacturers.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#e2eaea] overflow-hidden animate-pulse flex flex-col">
                <div className="h-[96px] bg-[#e2eaea]" />
                <div className="p-3 flex gap-2 items-center">
                  <div className="h-3 bg-[#e2eaea] rounded-full flex-1" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredManufacturers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredManufacturers.map((item, index) => (
              <BrandCard key={index} item={item} index={index} onClick={() => handleManufect(item)} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#374747]">No brands found</p>
            <p className="text-[13px] text-[#8faba9]">Try a different search term</p>
            <button onClick={() => { setSearchTerm(""); setFilteredManufacturers(manufacturers); }}
              className="flex items-center gap-2 bg-[#1e4a46] hover:bg-[#162c2a] text-white font-semibold text-[13px] px-5 h-9 rounded-xl transition-all duration-200">
              Clear search
            </button>
          </div>
        )}

        {/* View All */}
        {!isLoading && totalManufacturers > manufacturers.length && (
          <div className="flex justify-center mt-10">
            <button onClick={handleViewAll}
              className="flex items-center gap-2 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[14px] px-8 h-11 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(22,44,42,0.25)] hover:shadow-[0_5px_18px_rgba(255,96,30,0.4)] hover:-translate-y-0.5">
              View All Brands
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && !searchTerm && (
          <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`dot-${idx}`} className="text-[#8faba9] text-[13px] px-1">…</span>
                ) : (
                  <button key={item} onClick={() => setCurrentPage(item)}
                    className={`w-9 h-9 rounded-xl border-[1.5px] text-[13px] font-semibold flex items-center justify-center transition-all ${currentPage === item ? "bg-[#1e4a46] border-[#1e4a46] text-white shadow-[0_2px_8px_rgba(22,44,42,0.25)]" : "border-[#e2eaea] bg-white text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3]"}`}>
                    {item}
                  </button>
                )
              )}
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ShopBrand;